/**
 * FlahaSOIL v2 — Organization members page (Phase 9B-D).
 *
 * Lists ACTIVE / PENDING memberships in a table. ADMIN/OWNER see role
 * editors and a "Remove" button per row. The server enforces:
 *   - ADMIN cannot grant or revoke OWNER (UI hides those options).
 *   - The last OWNER cannot be demoted or removed (UI surfaces the
 *     resulting error inline; client-side prevention is best-effort).
 */
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	OrganizationRole,
	type OrganizationMemberDTO,
} from "@flaha/shared-types";
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../auth";
import { OrgAdminTabs } from "../features/organizations/components/OrgAdminTabs";
import { useActiveOrgAdmin } from "../features/organizations/useActiveOrgAdmin";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";

const ROLE_OPTIONS_OWNER: OrganizationRole[] = [
	OrganizationRole.OWNER,
	OrganizationRole.ADMIN,
	OrganizationRole.AGRONOMIST,
	OrganizationRole.LAB_TECHNICIAN,
	OrganizationRole.CONSULTANT,
	OrganizationRole.VIEWER,
];
// Phase 11 — ADMIN may only assign roles strictly below ADMIN.
const ROLE_OPTIONS_ADMIN = ROLE_OPTIONS_OWNER.filter(
	(r) =>
		r !== OrganizationRole.OWNER && r !== OrganizationRole.ADMIN
);

export function OrganizationMembersPage() {
	const { organizationId, isAdmin, isOwner } = useActiveOrgAdmin();
	const { user } = useAuth();
	const [members, setMembers] = useState<OrganizationMemberDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [pendingUserId, setPendingUserId] = useState<string | null>(null);

	usePageHeader({
		title: "Members",
		subtitle: members ? `${members.length} member(s)` : "Members",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Organization" },
			{ label: "Members" },
		],
	});

	const load = useCallback(() => {
		if (!organizationId) return;
		setError(null);
		setMembers(null);
		getApiClient()
			.listOrganizationMembers(organizationId)
			.then((res) => setMembers(res.members))
			.catch((err: unknown) =>
				setError(err instanceof Error ? err.message : String(err))
			);
	}, [organizationId]);

	useEffect(() => {
		load();
	}, [load]);

	const handleRoleChange = async (userId: string, nextRole: OrganizationRole) => {
		if (!organizationId) return;
		setPendingUserId(userId);
		setError(null);
		try {
			await getApiClient().patchMembership(organizationId, userId, {
				role: nextRole,
			});
			load();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			setPendingUserId(null);
		}
	};

	const handleRemove = async (userId: string) => {
		if (!organizationId) return;
		if (!window.confirm("Remove this member from the organization?")) return;
		setPendingUserId(userId);
		setError(null);
		try {
			await getApiClient().removeMembership(organizationId, userId);
			load();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			setPendingUserId(null);
		}
	};

	const handleLeave = async () => {
		if (!organizationId) return;
		if (!window.confirm("Leave this organization?")) return;
		setError(null);
		try {
			await getApiClient().leaveOrganization(organizationId);
			load();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		}
	};

	const handleTransfer = async (targetUserId: string) => {
		if (!organizationId) return;
		if (
			!window.confirm(
				"Transfer ownership to this member? You will become an ADMIN. Type confirmation is enforced as TRANSFER on the server."
			)
		) {
			return;
		}
		setPendingUserId(targetUserId);
		setError(null);
		try {
			await getApiClient().transferOwnership(organizationId, {
				targetUserId,
				confirmation: "TRANSFER",
			});
			load();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			setPendingUserId(null);
		}
	};

	if (!organizationId) {
		return (
			<Alert severity="info">
				No active organization. Pick one from the tenant switcher above.
			</Alert>
		);
	}

	const roleOptions = isOwner ? ROLE_OPTIONS_OWNER : ROLE_OPTIONS_ADMIN;

	return (
		<Box>
			<OrgAdminTabs organizationId={organizationId} isAdmin={isAdmin} />
			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}
			{!members ? (
				<CircularProgress size={24} />
			) : (
				<GlassSurface intensity="standard" depth="raised" sx={{ p: 0, overflow: "hidden" }}>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Role</TableCell>
									<TableCell>Status</TableCell>
									<TableCell align="right" />
								</TableRow>
							</TableHead>
							<TableBody>
								{members.length === 0 && (
									<TableRow>
										<TableCell colSpan={5}>
											<Typography
												variant="body2"
												sx={{
													py: 2,
													textAlign: "center",
													color: "workspace.textSecondary",
												}}
											>
												No members yet. Invite teammates to collaborate on
												this organization.
											</Typography>
										</TableCell>
									</TableRow>
								)}
								{members.map((m) => {
									const isTargetOwner = m.role === OrganizationRole.OWNER;
									const canEdit =
										isAdmin && (isOwner || !isTargetOwner);
									return (
										<TableRow key={m.id}>
											<TableCell>{m.userDisplayName}</TableCell>
											<TableCell>{m.userEmail}</TableCell>
											<TableCell>
												{canEdit ? (
													<Select
														size="small"
														value={m.role}
														disabled={pendingUserId === m.userId}
														onChange={(e) =>
															handleRoleChange(
																m.userId,
																e.target.value as OrganizationRole
															)
														}
													>
														{roleOptions.map((r) => (
															<MenuItem key={r} value={r}>
																{r}
															</MenuItem>
														))}
													</Select>
												) : (
													m.role
												)}
											</TableCell>
											<TableCell>
												<Chip
													label={m.status}
													size="small"
													color={
														m.status === "ACTIVE" ? "success" : "default"
													}
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Stack
													direction="row"
													spacing={0.5}
													justifyContent="flex-end"
												>
													{isOwner &&
														m.userId !== user?.id &&
														m.status === "ACTIVE" && (
															<Button
																size="small"
																variant="text"
																disabled={pendingUserId === m.userId}
																onClick={() => void handleTransfer(m.userId)}
															>
																Make owner
															</Button>
														)}
													{m.userId === user?.id && (
														<Button
															size="small"
															variant="text"
															color="warning"
															onClick={() => void handleLeave()}
														>
															Leave
														</Button>
													)}
													{canEdit && m.userId !== user?.id && (
														<Tooltip title="Remove member from organization">
															<span>
																<IconButton
																	size="small"
																	color="error"
																	aria-label={`Remove ${m.userEmail}`}
																	disabled={pendingUserId === m.userId}
																	onClick={() => handleRemove(m.userId)}
																>
																	<DeleteOutlineIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
													)}
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</GlassSurface>
			)}
			{isAdmin && (
				<Button
					variant="text"
					sx={{ mt: 2 }}
					href={`/organizations/${organizationId}/invitations`}
				>
					Manage invitations →
				</Button>
			)}
		</Box>
	);
}
